import type {
  ApiEnvelope,
  CreatorPostListResponse,
  CreatorListResponse,
  CurrentContextResponse,
  HealthResponse,
  ResearchSeedResponse,
  StorypilotFixtureFlowResponse,
  StorypilotDiagnosticsResponse,
  TikTokAccountListResponse
} from "../shared/contracts";

const fixtureSessionKey = "tiktok-srp-ops.fixture-session";

export function readFixtureSession(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem(fixtureSessionKey);
}

export function writeFixtureSession(value: string | null): void {
  if (typeof window === "undefined") {
    return;
  }

  if (value) {
    window.localStorage.setItem(fixtureSessionKey, value);
    return;
  }

  window.localStorage.removeItem(fixtureSessionKey);
}

export function storyOsLoginUrl(returnTo?: string): string {
  const fallbackReturnTo =
    typeof window === "undefined" ? "/" : `${window.location.pathname}${window.location.search}`;
  const url = new URL("/api/auth/storyos/start", typeof window === "undefined" ? "https://tiktok-srp.local.storyos.test" : window.location.origin);
  url.searchParams.set("return_to", returnTo ?? fallbackReturnTo);
  return `${url.pathname}${url.search}`;
}

async function apiGet<T>(path: string): Promise<ApiEnvelope<T>> {
  const headers = new Headers();
  const fixtureSession = readFixtureSession();
  if (fixtureSession) {
    headers.set("x-srp-fixture-session", fixtureSession);
  }

  const response = await fetch(path, {
    headers,
    cache: "no-store"
  });

  return (await response.json()) as ApiEnvelope<T>;
}

async function apiPost<T>(path: string): Promise<ApiEnvelope<T>> {
  const headers = new Headers();
  const fixtureSession = readFixtureSession();
  if (fixtureSession) {
    headers.set("x-srp-fixture-session", fixtureSession);
  }

  const response = await fetch(path, {
    method: "POST",
    headers,
    cache: "no-store"
  });

  return (await response.json()) as ApiEnvelope<T>;
}

export async function logoutProductSession(): Promise<void> {
  await fetch("/api/auth/logout", {
    method: "POST",
    cache: "no-store"
  });
}

export async function loadHealth(): Promise<ApiEnvelope<HealthResponse>> {
  return apiGet<HealthResponse>("/api/health");
}

export async function loadCurrentContext(): Promise<ApiEnvelope<CurrentContextResponse>> {
  return apiGet<CurrentContextResponse>("/api/current-context");
}

export async function loadStorypilotDiagnostics(): Promise<ApiEnvelope<StorypilotDiagnosticsResponse>> {
  return apiGet<StorypilotDiagnosticsResponse>("/api/diagnostics/storypilot");
}

export async function loadCreators(): Promise<ApiEnvelope<CreatorListResponse>> {
  return apiGet<CreatorListResponse>("/api/creators");
}

export async function loadTikTokAccounts(): Promise<ApiEnvelope<TikTokAccountListResponse>> {
  return apiGet<TikTokAccountListResponse>("/api/tiktok-accounts");
}

export async function loadCreatorPosts(): Promise<ApiEnvelope<CreatorPostListResponse>> {
  return apiGet<CreatorPostListResponse>("/api/posts");
}

export async function seedResearchCreators(): Promise<ApiEnvelope<ResearchSeedResponse>> {
  return apiPost<ResearchSeedResponse>("/api/seed/research-creators");
}

export async function runStorypilotFixtureFlow(): Promise<ApiEnvelope<StorypilotFixtureFlowResponse>> {
  return apiPost<StorypilotFixtureFlowResponse>("/api/storypilot/fixture-flow");
}
